import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Printer,
  FileSpreadsheet,
  Calendar,
  Truck,
  Coins,
  CheckCircle2,
  PackageCheck,
  Search,
  RefreshCw,
  CheckSquare,
  Square,
  Route,
  X,
  FileText,
} from 'lucide-react';
import {
  fetchShippingRunsheets,
  fetchShippingRunsheetDetail,
  getRunsheetExportUrl,
  type RunsheetTour,
  type RunsheetDetailResponse,
} from '../api.js';
import { PageHeader } from '../ui/PageHeader.js';
import { Button } from '../ui/Button.js';
import { Panel } from '../ui/panels.js';
import { TableShell, Table, THead, TBody, Tr, Th, Td } from '../ui/table.js';
import { Loading } from '../ui/feedback.js';
import { formatTnd } from '../lib/format.js';
import { cn } from '../ui/cn.js';

const MONTH_OPTIONS = [
  { value: 0, label: 'Tous les mois (Année complète)', name: 'Année Complète' },
  { value: 1, label: '01 - Janvier', name: 'Janvier' },
  { value: 2, label: '02 - Février', name: 'Février' },
  { value: 3, label: '03 - Mars', name: 'Mars' },
  { value: 4, label: '04 - Avril', name: 'Avril' },
  { value: 5, label: '05 - Mai', name: 'Mai' },
  { value: 6, label: '06 - Juin', name: 'Juin' },
  { value: 7, label: '07 - Juillet', name: 'Juillet' },
  { value: 8, label: '08 - Août', name: 'Août' },
  { value: 9, label: '09 - Septembre', name: 'Septembre' },
  { value: 10, label: '10 - Octobre', name: 'Octobre' },
  { value: 11, label: '11 - Novembre', name: 'Novembre' },
  { value: 12, label: '12 - Décembre', name: 'Décembre' },
];

const YEAR_OPTIONS = [2026, 2025, 2024, 2023];

const DEFAULT_SHIPARON_STATUSES: Array<{ id: number; label: string; count: number }> = [
  { id: 2, label: 'Livré', count: 0 },
  { id: 1, label: 'En cours', count: 0 },
  { id: 0, label: 'En attente', count: 0 },
  { id: 99, label: 'Non reçu', count: 0 },
  { id: 31, label: 'Rtn définitif', count: 0 },
  { id: 7, label: 'Rtn client/agence', count: 0 },
  { id: 11, label: 'Rtn dépôt', count: 0 },
  { id: 6, label: 'Annulé/Supprimé', count: 0 },
  { id: 5, label: 'Rtn expéditeur', count: 0 },
  { id: 8, label: 'Au magasin/dépôt', count: 0 },
  { id: 20, label: 'À vérifier', count: 0 },
];

const DEFAULT_WOO_STATUSES: Array<{ slug: string; label: string; count: number }> = [
  { slug: 'completed', label: 'Terminée (Complétée)', count: 0 },
  { slug: 'en-cours-de-prepa', label: 'En cours de préparation', count: 0 },
  { slug: 'cancelled', label: 'Annulée', count: 0 },
  { slug: 'on-hold', label: 'En attente', count: 0 },
  { slug: 'unmatched', label: 'Sans commande (Non liée)', count: 0 },
];

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const clean = iso.split('T')[0];
  const parts = clean?.split('-');
  if (parts && parts.length === 3 && parts[0]?.length === 4) {
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }
  return iso;
}

export function PaymentSlipsPage(): React.ReactElement {
  const currentDate = new Date();
  const currentYear = currentDate.getUTCFullYear();
  const currentMonth = currentDate.getUTCMonth() + 1; // 1-12

  // Primary Filters
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);
  const [selectedMonth, setSelectedMonth] = useState<number>(currentMonth);
  const [carrier, setCarrier] = useState<string>('all');
  const [customFeeTtc, setCustomFeeTtc] = useState<string>('6.000');
  const [searchTable, setSearchTable] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'validated' | 'in_progress'>('all');

  // Checkboxes Checklists States (Multi-Select Livreurs, Statuts Shiparon, Statuts Woo)
  const [selectedCarriers, setSelectedCarriers] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<number[]>([]);
  const [selectedWooStatuses, setSelectedWooStatuses] = useState<string[]>([]);

  // Accordion Expand/Collapse States
  const [carriersExpanded, setCarriersExpanded] = useState<boolean>(true);
  const [statusesExpanded, setStatusesExpanded] = useState<boolean>(true);
  const [wooStatusesExpanded, setWooStatusesExpanded] = useState<boolean>(true);

  // Active runsheet detail modal/drawer
  const [selectedRunsheetId, setSelectedRunsheetId] = useState<number | null>(null);

  const monthInfo = useMemo(() => {
    return MONTH_OPTIONS.find((m) => m.value === selectedMonth) ?? MONTH_OPTIONS[0]!;
  }, [selectedMonth]);

  const unitFeeTtc = useMemo(() => {
    const val = parseFloat(customFeeTtc);
    return !Number.isNaN(val) && val >= 0 ? val : 6.0;
  }, [customFeeTtc]);

  // Construct comma-separated livreurs parameter for multi-select
  const livreurParam = useMemo(() => {
    if (selectedCarriers.length === 0) {
      return carrier !== 'all' ? carrier : undefined;
    }
    if (selectedCarriers.includes('__none__')) return '__none__';
    return selectedCarriers.join(',');
  }, [selectedCarriers, carrier]);

  const statusesParam = useMemo(() => {
    if (selectedStatuses.length === 0) return undefined;
    if (selectedStatuses.includes(-999)) return '__none__';
    return selectedStatuses.join(',');
  }, [selectedStatuses]);

  const wooStatusesParam = useMemo(() => {
    if (selectedWooStatuses.length === 0) return undefined;
    if (selectedWooStatuses.includes('__none__')) return '__none__';
    return selectedWooStatuses.join(',');
  }, [selectedWooStatuses]);

  // Query Backend for all runsheets matching year, month, multi-livreurs, statuses and unitFee
  const runsheetsQuery = useQuery({
    queryKey: [
      'shipping-payment-slips-runsheets',
      selectedYear,
      selectedMonth,
      livreurParam,
      unitFeeTtc,
      statusFilter,
      statusesParam,
      wooStatusesParam,
      searchTable,
    ],
    queryFn: () =>
      fetchShippingRunsheets({
        year: String(selectedYear),
        month: selectedMonth === 0 ? 'all' : String(selectedMonth),
        livreur: livreurParam,
        unitFee: unitFeeTtc,
        status: statusFilter,
        statuses: statusesParam,
        wooStatuses: wooStatusesParam,
        search: searchTable.trim() || undefined,
        limit: 1000,
        page: 1,
      }),
    staleTime: 30_000,
  });

  const runsheetsData = runsheetsQuery.data;
  const tours: readonly RunsheetTour[] = useMemo(() => runsheetsData?.tours ?? [], [runsheetsData?.tours]);
  const summary = runsheetsData?.summary;
  const couriersList = runsheetsData?.couriersList ?? [];

  const availableStatuses = useMemo(() => {
    if (runsheetsData?.availableStatuses && runsheetsData.availableStatuses.length > 0) {
      return runsheetsData.availableStatuses;
    }
    return DEFAULT_SHIPARON_STATUSES;
  }, [runsheetsData?.availableStatuses]);

  const availableWooStatuses = useMemo(() => {
    if (runsheetsData?.availableWooStatuses && runsheetsData.availableWooStatuses.length > 0) {
      return runsheetsData.availableWooStatuses;
    }
    return DEFAULT_WOO_STATUSES;
  }, [runsheetsData?.availableWooStatuses]);

  // Couriers partition into dedicated livreurs and prestataires
  const prestaCouriers = useMemo(() => couriersList.filter((c) => c.type === 'presta'), [couriersList]);
  const livreurCouriers = useMemo(() => couriersList.filter((c) => c.type === 'livreur'), [couriersList]);

  // Carrier Checkbox Handlers
  const toggleCarrier = (key: string) => {
    setSelectedCarriers((prev) => {
      if (prev.length === 0) {
        return couriersList.map((c) => c.key).filter((k) => k !== key);
      }
      if (prev.includes(key)) {
        return prev.filter((k) => k !== key);
      }
      const next = [...prev, key];
      if (next.length === couriersList.length) return [];
      return next;
    });
  };

  const isCarrierChecked = (key: string): boolean => {
    if (selectedCarriers.length === 0) return true;
    return selectedCarriers.includes(key);
  };

  const selectAllCarriers = () => setSelectedCarriers([]);
  const selectPrestaOnly = () => setSelectedCarriers(prestaCouriers.map((c) => c.key));
  const selectLivreursOnly = () => setSelectedCarriers(livreurCouriers.map((c) => c.key));
  const clearAllCarriers = () => setSelectedCarriers(['__none__']);

  // Shiparon Statuses Checkbox Handlers
  const toggleStatus = (id: number) => {
    setSelectedStatuses((prev) => {
      if (prev.length === 0) {
        return availableStatuses.map((s) => s.id).filter((sId) => sId !== id);
      }
      if (prev.includes(id)) {
        return prev.filter((sId) => sId !== id);
      }
      const next = [...prev, id];
      if (next.length === availableStatuses.length) return [];
      return next;
    });
  };

  const isStatusChecked = (id: number): boolean => {
    if (selectedStatuses.length === 0) return true;
    return selectedStatuses.includes(id);
  };

  const selectAllStatuses = () => setSelectedStatuses([]);
  const selectDeliveredOnly = () => setSelectedStatuses([2, 10, 32]);
  const selectReturnsOnly = () => setSelectedStatuses([31, 5, 7, 11, 30]);
  const selectInTransitOnly = () => setSelectedStatuses([1, 8, 15, 16]);
  const clearAllStatuses = () => setSelectedStatuses([-999]);

  // WooCommerce Statuses Checkbox Handlers
  const toggleWooStatus = (slug: string) => {
    setSelectedWooStatuses((prev) => {
      if (prev.length === 0) {
        return availableWooStatuses.map((s) => s.slug).filter((sSlug) => sSlug !== slug);
      }
      if (prev.includes(slug)) {
        return prev.filter((sSlug) => sSlug !== slug);
      }
      const next = [...prev, slug];
      if (next.length === availableWooStatuses.length) return [];
      return next;
    });
  };

  const isWooStatusChecked = (slug: string): boolean => {
    if (selectedWooStatuses.length === 0) return true;
    return selectedWooStatuses.includes(slug);
  };

  const selectAllWooStatuses = () => setSelectedWooStatuses([]);
  const selectCompletedWooOnly = () => setSelectedWooStatuses(['completed']);
  const selectPrepaWooOnly = () => setSelectedWooStatuses(['en-cours-de-prepa']);
  const selectCancelledWooOnly = () => setSelectedWooStatuses(['cancelled']);
  const selectUnmatchedWooOnly = () => setSelectedWooStatuses(['unmatched']);
  const clearAllWooStatuses = () => setSelectedWooStatuses(['__none__']);

  // Active carrier display label
  const cleanCarrierName = useMemo(() => {
    if (selectedCarriers.length === 1) {
      const found = couriersList.find((c) => c.key === selectedCarriers[0]);
      if (found) return found.label;
      return selectedCarriers[0];
    }
    if (selectedCarriers.length > 1 && selectedCarriers.length < couriersList.length) {
      return `${selectedCarriers.length} transporteurs sélectionnés`;
    }
    if (carrier !== 'all') {
      const found = couriersList.find((c) => c.key === carrier);
      if (found) return found.label;
      return carrier;
    }
    return 'Tous les transporteurs & coursiers';
  }, [selectedCarriers, carrier, couriersList]);

  // KPIs across the entire filtered dataset from summary
  const totalDelivered = summary ? summary.totalDelivered : tours.reduce((acc, t) => acc + t.deliveredCount, 0);
  const totalPackages = summary ? summary.totalPackages : tours.reduce((acc, t) => acc + t.packageCount, 0);
  const totalDeliveredCod = summary ? summary.totalDeliveredCod : tours.reduce((acc, t) => acc + t.deliveredCod, 0);
  const totalDeliveryFee = Math.round(totalDelivered * unitFeeTtc * 1000) / 1000;
  const totalNet = Math.round((totalDeliveredCod - totalDeliveryFee) * 1000) / 1000;
  const totalValidated = summary ? summary.totalValidated : tours.filter((t) => t.validated).length;
  const totalInProgress = summary ? summary.totalInProgress : tours.filter((t) => !t.validated).length;
  const deliveryPct = totalPackages > 0 ? Math.round((totalDelivered / totalPackages) * 100) : 0;

  // Export URL for all filtered runsheets
  const exportUrl = useMemo(() => {
    return getRunsheetExportUrl({
      year: String(selectedYear),
      month: selectedMonth === 0 ? 'all' : String(selectedMonth),
      livreur: livreurParam,
      unitFee: unitFeeTtc,
      status: statusFilter,
      statuses: statusesParam,
      wooStatuses: wooStatusesParam,
      search: searchTable.trim() || undefined,
    });
  }, [selectedYear, selectedMonth, livreurParam, unitFeeTtc, statusFilter, statusesParam, wooStatusesParam, searchTable]);

  // Dedicated Export URL for Full Year
  const exportFullYearUrl = useMemo(() => {
    return getRunsheetExportUrl({
      year: String(selectedYear),
      month: 'all',
      livreur: livreurParam,
      unitFee: unitFeeTtc,
      status: statusFilter,
      statuses: statusesParam,
      wooStatuses: wooStatusesParam,
      search: searchTable.trim() || undefined,
    });
  }, [selectedYear, livreurParam, unitFeeTtc, statusFilter, statusesParam, wooStatusesParam, searchTable]);

  // Query Detail for selected runsheet (Modal / A4 print)
  const detailQuery = useQuery<RunsheetDetailResponse>({
    queryKey: ['shipping-runsheet-detail-modal', selectedRunsheetId, unitFeeTtc],
    queryFn: () =>
      fetchShippingRunsheetDetail(selectedRunsheetId!, {
        limit: 200,
        page: 1,
      }),
    enabled: selectedRunsheetId !== null && selectedRunsheetId > 0,
  });

  const detailData = detailQuery.data;
  const detailHeader = detailData?.header;
  const detailParcels = detailData?.parcels ?? [];

  return (
    <div className="space-y-5 animate-fade pb-12">
      <style>{`
        @media print {
          body { background: white !important; color: black !important; font-size: 10px !important; }
          header, nav, aside, .no-print, button, input, select { display: none !important; }
          .print-header { display: block !important; margin-bottom: 12px; border-bottom: 2px solid #065f46; padding-bottom: 6px; }
          .print-badge-encours { color: #991b1b !important; font-weight: bold !important; }
          .print-badge-valid { color: #065f46 !important; font-weight: bold !important; }
          table { width: 100% !important; border-collapse: collapse !important; }
          th, td { border: 1px solid #d1d5db !important; padding: 4px 6px !important; }
          .en-cours-row { background-color: #fee2e2 !important; color: #991b1b !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          @page { size: landscape; margin: 8mm; }
        }
        .print-header { display: none; }
      `}</style>

      {/* 1. Header (Hidden when printing A4) */}
      <div className="no-print">
        <PageHeader
          index="S14"
          eyebrow="Gestion Logistique · CA &amp; Règlements Coursiers"
          title={
            selectedMonth === 0
              ? `Bordereaux de Paiement Coursiers — Année ${selectedYear} (12 Mois)`
              : `Bordereaux de Paiement Coursiers — ${monthInfo.name} ${selectedYear}`
          }
          purpose="Sélectionnez une année, un mois (ou l'année complète) et cochez vos livreurs et transporteurs (1, 2 ou plus) pour lister l'ensemble des feuilles de route (runsheets), déduire les frais de livraison TTC et arrêter le net à reverser."
          actions={
            <div className="flex flex-wrap items-center gap-2">
              <a href={exportFullYearUrl} download>
                <Button
                  variant="outline"
                  size="sm"
                  className="font-semibold shadow-2xs gap-1.5 border-emerald-500 bg-emerald-700 text-white hover:bg-emerald-800 hover:text-white"
                  title={`Télécharger toutes les feuilles de route de l'année ${selectedYear} en Excel`}
                >
                  <FileSpreadsheet className="h-4 w-4 text-emerald-100" />
                  <span>Exporter l'Année Complète ({selectedYear})</span>
                </Button>
              </a>
              <a href={exportUrl} download>
                <Button
                  variant="outline"
                  size="sm"
                  className="font-semibold shadow-2xs gap-1.5 border-emerald-300 bg-emerald-50 text-emerald-900 hover:bg-emerald-100"
                  title="Exporter la sélection en cours (.xlsx)"
                >
                  <FileSpreadsheet className="h-4 w-4 text-emerald-700" />
                  <span>
                    {selectedMonth === 0
                      ? `Exporter la Sélection (${selectedYear})`
                      : `Exporter ${monthInfo.name} (.xlsx)`}
                  </span>
                </Button>
              </a>
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.print()}
                className="gap-1.5 font-semibold"
              >
                <Printer className="h-4 w-4" />
                <span>Imprimer / PDF</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => void runsheetsQuery.refetch()}
                disabled={runsheetsQuery.isFetching}
                title="Rafraîchir les données"
              >
                <RefreshCw className={cn('h-4 w-4', runsheetsQuery.isFetching && 'animate-spin text-emerald-700')} />
              </Button>
            </div>
          }
        />
      </div>

      {/* Printable Report Header */}
      <div className="print-header">
        <h1 className="text-xl font-bold text-ink">MaPara.tn · Bordereaux de Paiement Coursiers</h1>
        <p className="text-xs text-ink-soft">
          Période : <strong>{selectedMonth === 0 ? `Année Complète ${selectedYear}` : `${monthInfo.name} ${selectedYear}`}</strong> · Transporteur(s) : <strong>{cleanCarrierName}</strong> · Tarif appliqué : <strong>{unitFeeTtc.toFixed(3)} DT TTC</strong> / colis · Date export : {new Date().toLocaleDateString('fr-FR')}
        </p>
        <p className="text-xs font-semibold text-ink mt-1">
          Tournées : {tours.length} ({totalValidated} validées / {totalInProgress} en cours) · Colis livrés : {totalDelivered} / {totalPackages} ({deliveryPct} %) · Total COD : {formatTnd(totalDeliveredCod)} · Frais Livraison : {formatTnd(totalDeliveryFee)} · <strong>Net Total à Reverser : {formatTnd(totalNet)}</strong>
        </p>
      </div>

      {/* 2. Filters Control Bar (Hidden when printing A4) */}
      <div className="no-print">
        <Panel className="p-4 space-y-4 border-border bg-card shadow-xs">
          {/* Primary Row: Year, Month, Carrier Select & Fee */}
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border/60 pb-3">
            {/* Courier Dropdown */}
            <div className="flex items-center gap-2.5 min-w-[260px] flex-1 max-w-sm">
              <div className="p-2 bg-emerald-100/70 text-emerald-800 rounded-lg shrink-0">
                <Truck className="h-4 w-4" />
              </div>
              <div className="flex-1">
                <label htmlFor="carrier-select" className="block text-[10px] font-bold uppercase tracking-wider text-ink-ghost">
                  Coursier Principal (Filtre rapide)
                </label>
                <select
                  id="carrier-select"
                  value={carrier}
                  onChange={(e) => {
                    setCarrier(e.target.value);
                    if (e.target.value !== 'all') {
                      setSelectedCarriers([e.target.value]);
                    } else {
                      setSelectedCarriers([]);
                    }
                  }}
                  className="mt-0.5 w-full rounded-lg border border-border bg-card px-2.5 py-1.5 text-xs font-semibold text-ink shadow-2xs focus:border-emerald-600 focus:outline-none"
                >
                  <option value="all">Tous les livreurs &amp; transporteurs</option>
                  {prestaCouriers.length > 0 && (
                    <optgroup label="Sociétés de livraison (Prestataires)">
                      {prestaCouriers.map((c) => (
                        <option key={c.key} value={c.key}>
                          {c.label} ({c.count.toLocaleString('fr-FR')} colis)
                        </option>
                      ))}
                    </optgroup>
                  )}
                  {livreurCouriers.length > 0 && (
                    <optgroup label="Livreurs &amp; Tournées Dédiées">
                      {livreurCouriers.map((c) => (
                        <option key={c.key} value={c.key}>
                          {c.label} ({c.count.toLocaleString('fr-FR')} colis)
                        </option>
                      ))}
                    </optgroup>
                  )}
                </select>
              </div>
            </div>

            {/* Year Selector */}
            <div className="flex items-center gap-2 bg-cream/40 p-2 rounded-lg border border-border min-w-[130px]">
              <Calendar className="h-4 w-4 text-ink-soft shrink-0" />
              <div className="flex-1">
                <label htmlFor="year-select" className="block text-[10px] font-bold uppercase tracking-wider text-ink-ghost">
                  Année
                </label>
                <select
                  id="year-select"
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                  className="mt-0.5 w-full rounded-md border border-border/80 bg-card px-2 py-1 font-mono text-xs font-bold text-ink focus:border-emerald-600 focus:outline-none"
                >
                  {YEAR_OPTIONS.map((yr) => (
                    <option key={yr} value={yr}>
                      {yr}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Month Selector */}
            <div className="flex items-center gap-2 bg-cream/40 p-2 rounded-lg border border-border min-w-[170px]">
              <Calendar className="h-4 w-4 text-emerald-700 shrink-0" />
              <div className="flex-1">
                <label htmlFor="month-select" className="block text-[10px] font-bold uppercase tracking-wider text-ink-ghost">
                  Mois
                </label>
                <select
                  id="month-select"
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(Number(e.target.value))}
                  className="mt-0.5 w-full rounded-md border border-border/80 bg-card px-2 py-1 text-xs font-bold text-ink focus:border-emerald-600 focus:outline-none"
                >
                  {MONTH_OPTIONS.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Delivery Fee Input TTC */}
            <div className="flex items-center gap-2 bg-cream/40 p-2 rounded-lg border border-border min-w-[160px]">
              <Coins className="h-4 w-4 text-emerald-700 shrink-0" />
              <div>
                <label htmlFor="fee-input" className="block text-[10px] font-bold uppercase tracking-wider text-ink-ghost">
                  Frais livraison TTC / colis
                </label>
                <div className="flex items-center gap-1 mt-0.5">
                  <input
                    id="fee-input"
                    type="number"
                    step="0.100"
                    min="0"
                    value={customFeeTtc}
                    onChange={(e) => setCustomFeeTtc(e.target.value)}
                    placeholder="6.000"
                    className="w-20 rounded-md border border-border/80 bg-card px-2 py-1 font-mono text-xs font-bold text-ink focus:border-emerald-600 focus:outline-none"
                  />
                  <span className="text-xs font-bold text-ink-soft">DT</span>
                  {customFeeTtc !== '6.000' && (
                    <button
                      type="button"
                      onClick={() => setCustomFeeTtc('6.000')}
                      className="text-[10px] text-ink-ghost hover:text-emerald-700 ml-1 underline cursor-pointer"
                      title="Réinitialiser à 6.000 DT"
                    >
                      Reset
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Search Input in Table */}
            <div className="flex items-center gap-2 min-w-[220px]">
              <div className="relative w-full">
                <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-ink-ghost" />
                <input
                  type="text"
                  value={searchTable}
                  onChange={(e) => setSearchTable(e.target.value)}
                  placeholder="Filtrer N° runsheet, livreur…"
                  className="w-full pl-8 pr-3 py-1.5 text-xs rounded-lg border border-border bg-card text-ink placeholder:text-ink-ghost focus:border-emerald-600 focus:outline-none"
                />
              </div>
            </div>
          </div>

          {/* Section 2: Checkboxes Multi-Select Courriers & Transporteurs (1, 2 ou plus) */}
          <div className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Truck className="h-4 w-4 text-emerald-700" />
                <span className="text-[11px] font-bold text-ink uppercase tracking-wider">
                  Courriers &amp; Transporteurs (
                  {selectedCarriers.length === 0
                    ? 'Tous cochés'
                    : `${selectedCarriers.length} sélectionné(s)`}
                  ) :
                </span>
                <button
                  type="button"
                  onClick={() => setCarriersExpanded((p) => !p)}
                  className="text-xs text-emerald-800 hover:underline font-semibold cursor-pointer"
                >
                  {carriersExpanded ? 'Réduire' : 'Déplier'}
                </button>
              </div>

              {/* Quick Presets Buttons */}
              <div className="flex flex-wrap items-center gap-1.5 text-xs">
                <Button
                  type="button"
                  variant="outline"
                  size="xs"
                  onClick={selectAllCarriers}
                  className={cn(selectedCarriers.length === 0 && 'bg-emerald-50 font-bold text-emerald-800 border-emerald-300')}
                >
                  Tous
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="xs"
                  onClick={selectLivreursOnly}
                  className={cn(
                    selectedCarriers.length === livreurCouriers.length &&
                      livreurCouriers.length > 0 &&
                      'bg-emerald-50 font-bold text-emerald-800 border-emerald-300',
                  )}
                >
                  Livreurs Dédiés
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="xs"
                  onClick={selectPrestaOnly}
                  className={cn(
                    selectedCarriers.length === prestaCouriers.length &&
                      prestaCouriers.length > 0 &&
                      'bg-emerald-50 font-bold text-emerald-800 border-emerald-300',
                  )}
                >
                  Prestataires
                </Button>
                <Button type="button" variant="outline" size="xs" onClick={clearAllCarriers}>
                  Aucun
                </Button>
              </div>
            </div>

            {carriersExpanded && (
              <div className="flex flex-wrap items-center gap-2 p-2.5 bg-cream/30 rounded-xl border border-border/70 max-h-40 overflow-y-auto">
                {couriersList.length === 0 ? (
                  <span className="text-xs text-ink-ghost italic">Chargement des transporteurs…</span>
                ) : (
                  couriersList.map((c) => {
                    const checked = isCarrierChecked(c.key);
                    return (
                      <label
                        key={c.key}
                        className={cn(
                          'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium cursor-pointer border transition-colors select-none',
                          checked
                            ? 'bg-card text-ink border-emerald-400/80 shadow-2xs font-semibold'
                            : 'bg-card/50 text-ink-ghost border-border hover:bg-card hover:text-ink-soft',
                        )}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleCarrier(c.key)}
                          className="sr-only"
                        />
                        {checked ? (
                          <CheckSquare className="h-3.5 w-3.5 text-emerald-700 shrink-0" />
                        ) : (
                          <Square className="h-3.5 w-3.5 text-ink-ghost shrink-0" />
                        )}
                        <span>{c.label}</span>
                        {c.count > 0 && (
                          <span className="text-[10px] px-1.5 py-0.2 rounded-full bg-cream text-ink-soft border border-border font-mono">
                            {c.count.toLocaleString('fr-FR')}
                          </span>
                        )}
                      </label>
                    );
                  })
                )}
              </div>
            )}
          </div>

          {/* Section 2b: Dynamic Shiparon Statuses Checklist */}
          <div className="space-y-2 pt-2 border-t border-border/50">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <span className="text-[11px] font-bold text-ink uppercase tracking-wider">
                  Statuts Shiparon ({selectedStatuses.length === 0 ? 'Tous cochés' : `${selectedStatuses.length} sélectionné(s)`}) :
                </span>
                <button
                  type="button"
                  onClick={() => setStatusesExpanded((p) => !p)}
                  className="text-xs text-emerald-800 hover:underline font-semibold cursor-pointer"
                >
                  {statusesExpanded ? 'Réduire' : 'Déplier'}
                </button>
              </div>

              {/* Quick Status Presets */}
              <div className="flex flex-wrap items-center gap-1.5 text-xs">
                <Button
                  type="button"
                  variant="outline"
                  size="xs"
                  onClick={selectAllStatuses}
                  className={cn(selectedStatuses.length === 0 && 'bg-emerald-50 font-bold text-emerald-800 border-emerald-300')}
                >
                  Tous
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="xs"
                  onClick={selectDeliveredOnly}
                  className={cn(selectedStatuses.length === 3 && selectedStatuses.includes(2) && 'bg-emerald-50 font-bold text-emerald-800 border-emerald-300')}
                >
                  Livrés
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="xs"
                  onClick={selectReturnsOnly}
                  className={cn(selectedStatuses.length === 5 && selectedStatuses.includes(31) && 'bg-rust-50 font-bold text-rust-800 border-rust-300')}
                >
                  Retours
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="xs"
                  onClick={selectInTransitOnly}
                  className={cn(selectedStatuses.length === 4 && selectedStatuses.includes(1) && 'bg-sky-50 font-bold text-sky-800 border-sky-300')}
                >
                  En cours / Transit
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="xs"
                  onClick={clearAllStatuses}
                >
                  Aucun
                </Button>
              </div>
            </div>

            {statusesExpanded && (
              <div className="flex flex-wrap items-center gap-2 p-2.5 bg-cream/30 rounded-xl border border-border/70 max-h-36 overflow-y-auto">
                {availableStatuses.map((st) => {
                  const checked = isStatusChecked(st.id);
                  return (
                    <label
                      key={st.id}
                      className={cn(
                        'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium cursor-pointer border transition-colors select-none',
                        checked
                          ? 'bg-card text-ink border-emerald-400/80 shadow-2xs font-semibold'
                          : 'bg-card/50 text-ink-ghost border-border hover:bg-card hover:text-ink-soft',
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleStatus(st.id)}
                        className="sr-only"
                      />
                      {checked ? (
                        <CheckSquare className="h-3.5 w-3.5 text-emerald-700 shrink-0" />
                      ) : (
                        <Square className="h-3.5 w-3.5 text-ink-ghost shrink-0" />
                      )}
                      <span>{st.label}</span>
                      {st.count > 0 && (
                        <span className="text-[10px] px-1.5 py-0.2 rounded-full bg-cream text-ink-soft border border-border font-mono">
                          {st.count.toLocaleString('fr-FR')}
                        </span>
                      )}
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          {/* Section 2c: Dynamic WooCommerce Order Statuses Checklist */}
          <div className="space-y-2 pt-2 border-t border-border/50">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <span className="text-[11px] font-bold text-ink uppercase tracking-wider">
                  Statuts Commande WooCommerce ({selectedWooStatuses.length === 0 ? 'Tous cochés' : `${selectedWooStatuses.length} sélectionné(s)`}) :
                </span>
                <button
                  type="button"
                  onClick={() => setWooStatusesExpanded((p) => !p)}
                  className="text-xs text-emerald-800 hover:underline font-semibold cursor-pointer"
                >
                  {wooStatusesExpanded ? 'Réduire' : 'Déplier'}
                </button>
              </div>

              {/* Quick Woo Status Presets */}
              <div className="flex flex-wrap items-center gap-1.5 text-xs">
                <Button
                  type="button"
                  variant="outline"
                  size="xs"
                  onClick={selectAllWooStatuses}
                  className={cn(selectedWooStatuses.length === 0 && 'bg-emerald-50 font-bold text-emerald-800 border-emerald-300')}
                >
                  Tous
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="xs"
                  onClick={selectCompletedWooOnly}
                  className={cn(selectedWooStatuses.length === 1 && selectedWooStatuses[0] === 'completed' && 'bg-emerald-50 font-bold text-emerald-800 border-emerald-300')}
                >
                  Complétées
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="xs"
                  onClick={selectPrepaWooOnly}
                  className={cn(selectedWooStatuses.length === 1 && selectedWooStatuses[0] === 'en-cours-de-prepa' && 'bg-blue-50 font-bold text-blue-800 border-blue-300')}
                >
                  En prépa
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="xs"
                  onClick={selectCancelledWooOnly}
                  className={cn(selectedWooStatuses.length === 1 && selectedWooStatuses[0] === 'cancelled' && 'bg-rose-50 font-bold text-rose-800 border-rose-300')}
                >
                  Annulées
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="xs"
                  onClick={selectUnmatchedWooOnly}
                  className={cn(selectedWooStatuses.length === 1 && selectedWooStatuses[0] === 'unmatched' && 'bg-zinc-100 font-bold text-zinc-800 border-zinc-300')}
                >
                  Sans commande
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="xs"
                  onClick={clearAllWooStatuses}
                >
                  Aucun
                </Button>
              </div>
            </div>

            {wooStatusesExpanded && (
              <div className="flex flex-wrap items-center gap-2 p-2.5 bg-cream/30 rounded-xl border border-border/70 max-h-36 overflow-y-auto">
                {availableWooStatuses.map((st) => {
                  const checked = isWooStatusChecked(st.slug);
                  return (
                    <label
                      key={st.slug}
                      className={cn(
                        'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium cursor-pointer border transition-colors select-none',
                        checked
                          ? 'bg-card text-ink border-emerald-400/80 shadow-2xs font-semibold'
                          : 'bg-card/50 text-ink-ghost border-border hover:bg-card hover:text-ink-soft',
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleWooStatus(st.slug)}
                        className="sr-only"
                      />
                      {checked ? (
                        <CheckSquare className="h-3.5 w-3.5 text-emerald-700 shrink-0" />
                      ) : (
                        <Square className="h-3.5 w-3.5 text-ink-ghost shrink-0" />
                      )}
                      <span>{st.label}</span>
                      {st.count > 0 && (
                        <span className="text-[10px] px-1.5 py-0.2 rounded-full bg-cream text-ink-soft border border-border font-mono">
                          {st.count.toLocaleString('fr-FR')}
                        </span>
                      )}
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          {/* Section 3: Statut Filtre (Tous / Validées / En cours) */}
          <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-border/50 text-xs">
            <span className="font-bold text-ink uppercase tracking-wider text-[11px] mr-1">
              Statut Clôture :
            </span>
            {(
              [
                ['all', 'Tous les runsheets (même en cours)'],
                ['validated', 'Clôturées (Validées) uniquement'],
                ['in_progress', 'En cours (Non clôturées en rouge)'],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => setStatusFilter(value)}
                className={cn(
                  'rounded-md px-2.5 py-1 text-xs font-medium transition-colors cursor-pointer border',
                  statusFilter === value
                    ? value === 'in_progress'
                      ? 'bg-rose-700 text-white border-rose-700 shadow-sm font-bold'
                      : 'bg-emerald-700 text-white border-emerald-700 shadow-sm font-bold'
                    : 'bg-card text-ink-soft border-border hover:bg-cream',
                )}
              >
                {label}
              </button>
            ))}
          </div>
        </Panel>
      </div>

      {/* 3. Monthly Summary KPIs (Matches Image 1) */}
      <div className="no-print">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5">
          {/* Card 1: Total Colis Livrés */}
          <div className="rounded-xl border border-border bg-card p-4 shadow-xs">
            <div className="flex items-center justify-between text-ink-ghost">
              <span className="text-[11px] font-bold uppercase tracking-wider">
                Total Colis Livrés ({selectedMonth === 0 ? `Année ${selectedYear}` : monthInfo.name})
              </span>
              <PackageCheck className="h-4 w-4 text-emerald-600" />
            </div>
            <div className="mt-2">
              <div className="text-2xl font-black font-mono text-emerald-700">
                {totalDelivered.toLocaleString('fr-FR')}
              </div>
              <div className="mt-1 text-xs text-ink-soft">
                Répartis sur <b>{tours.length}</b> feuilles de route ({totalValidated} validées / {totalInProgress} en cours)
              </div>
            </div>
          </div>

          {/* Card 2: COD Total Encaissé */}
          <div className="rounded-xl border border-border bg-card p-4 shadow-xs">
            <div className="flex items-center justify-between text-ink-ghost">
              <span className="text-[11px] font-bold uppercase tracking-wider">COD Total Encaissé</span>
              <Coins className="h-4 w-4 text-blue-600" />
            </div>
            <div className="mt-2">
              <div className="text-2xl font-black font-mono text-blue-700">
                {formatTnd(totalDeliveredCod)}
              </div>
              <div className="mt-1 text-xs text-ink-soft">Fonds bruts perçus par les coursiers</div>
            </div>
          </div>

          {/* Card 3: Frais Livraison TTC Déduits */}
          <div className="rounded-xl border border-border bg-card p-4 shadow-xs">
            <div className="flex items-center justify-between text-ink-ghost">
              <span className="text-[11px] font-bold uppercase tracking-wider">Frais Livraison TTC Déduits</span>
              <Coins className="h-4 w-4 text-amber-600" />
            </div>
            <div className="mt-2">
              <div className="text-2xl font-black font-mono text-amber-700">
                {formatTnd(totalDeliveryFee)}
              </div>
              <div className="mt-1 text-xs text-ink-soft font-mono">
                {totalDelivered.toLocaleString('fr-FR')} &times; {unitFeeTtc.toFixed(3)} DT
              </div>
            </div>
          </div>

          {/* Card 4: Net Total à Reverser */}
          <div className="rounded-xl border-2 border-emerald-600 bg-emerald-50/70 p-4 shadow-xs">
            <div className="flex items-center justify-between text-emerald-950">
              <span className="text-[11px] font-black uppercase tracking-wider">
                Net Total à Reverser ({selectedMonth === 0 ? `Année ${selectedYear}` : 'Mois'})
              </span>
              <CheckCircle2 className="h-5 w-5 text-emerald-700" />
            </div>
            <div className="mt-2">
              <div className="text-2xl font-black font-mono text-emerald-800">
                {formatTnd(totalNet)}
              </div>
              <div className="mt-1 text-[11px] font-semibold text-emerald-900">
                COD Encaissé − Frais TTC ({tours.length} bordereaux de tournée)
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 4. LIST ALL RUNSHEETS TABLE (Replaces the 10 3-day intervals) */}
      <Panel>
        <div className="p-4 border-b border-border flex flex-wrap items-center justify-between gap-3 no-print">
          <div className="flex items-center gap-2">
            <Route className="h-5 w-5 text-emerald-700" />
            <div>
              <h2 className="text-sm font-bold uppercase tracking-wider text-ink">
                Feuilles de Route (Runsheets) — {selectedMonth === 0 ? `Année ${selectedYear} (12 Mois)` : `${monthInfo.name} ${selectedYear}`}
              </h2>
              <p className="text-xs text-ink-soft">
                {cleanCarrierName} · <strong>{tours.length}</strong> tournée(s) au total ({totalValidated} validée(s), {totalInProgress} en cours)
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <a href={exportUrl} download>
              <Button variant="outline" size="sm" className="gap-1.5 font-semibold">
                <FileSpreadsheet className="h-4 w-4 text-emerald-700" />
                <span>Exporter ce Tableau (.xlsx)</span>
              </Button>
            </a>
          </div>
        </div>

        {runsheetsQuery.isLoading ? (
          <div className="py-16 text-center">
            <Loading label="Chargement des feuilles de route du mois…" />
          </div>
        ) : tours.length === 0 ? (
          <div className="py-16 text-center text-sm text-ink-soft">
            Aucune feuille de route trouvée pour {selectedMonth === 0 ? `l'année ${selectedYear}` : `${monthInfo.name} ${selectedYear}`} avec ces transporteurs.
          </div>
        ) : (
          <div className="p-3">
            <TableShell>
              <Table>
                <THead>
                  <Tr>
                    <Th>N° Runsheet</Th>
                    <Th>Date</Th>
                    <Th>Livreur / Transporteur</Th>
                    <Th>Agence</Th>
                    <Th align="right">Colis Embarqués</Th>
                    <Th align="right">Colis Livrés</Th>
                    <Th align="right">Taux Livr.</Th>
                    <Th align="right">COD Encaissé</Th>
                    <Th align="right">Frais Livraison TTC</Th>
                    <Th align="right">Net à Reverser</Th>
                    <Th>Statut</Th>
                    <Th className="no-print">Action</Th>
                  </Tr>
                </THead>
                <TBody>
                  {tours.map((t) => {
                    const isEnCours = !t.validated;
                    const rate = t.packageCount > 0 ? Math.round((t.deliveredCount / t.packageCount) * 100) : 0;

                    return (
                      <Tr
                        key={t.idRunsheet}
                        className={cn(
                          'transition-colors',
                          isEnCours
                            ? 'bg-rose-50/75 hover:bg-rose-100/80 border-l-4 border-l-rose-500 en-cours-row'
                            : 'hover:bg-cream/40',
                        )}
                      >
                        <Td>
                          <span
                            className={cn(
                              'inline-flex items-center gap-1.5 font-mono text-xs font-bold',
                              isEnCours ? 'text-rose-900 font-black' : 'text-ink',
                            )}
                          >
                            <Route className={cn('h-3.5 w-3.5', isEnCours ? 'text-rose-600' : 'text-emerald-700')} />
                            #{t.idRunsheet}
                          </span>
                        </Td>
                        <Td>
                          <span className="text-xs text-ink-soft">
                            {formatDate(t.tourDate)}
                          </span>
                        </Td>
                        <Td>
                          <div className={cn('text-xs font-semibold', isEnCours ? 'text-rose-950 font-bold' : 'text-ink')}>
                            {t.livreurNom ?? '—'}
                          </div>
                          {t.matricule && (
                            <div className="font-mono text-[10px] text-ink-faint">{t.matricule}</div>
                          )}
                        </Td>
                        <Td>
                          <span className="text-xs text-ink-soft">{t.agence ?? '—'}</span>
                        </Td>
                        <Td align="right">
                          <span className="font-mono text-xs font-semibold text-ink">
                            {t.packageCount.toLocaleString('fr-FR')}
                          </span>
                        </Td>
                        <Td align="right">
                          <span className={cn('font-mono text-xs font-bold', isEnCours ? 'text-rose-900' : 'text-emerald-800')}>
                            {t.deliveredCount.toLocaleString('fr-FR')}
                          </span>
                        </Td>
                        <Td align="right">
                          <span className="font-mono text-xs text-ink-soft">
                            {rate} %
                          </span>
                        </Td>
                        <Td align="right">
                          <span className={cn('font-mono text-xs font-bold', isEnCours ? 'text-rose-900' : 'text-blue-900')}>
                            {formatTnd(t.deliveredCod)}
                          </span>
                        </Td>
                        <Td align="right">
                          <span
                            className="font-mono text-xs font-semibold text-amber-700"
                            title={`Tarif : ${t.unitFee.toFixed(3)} DT / colis`}
                          >
                            {formatTnd(t.deliveryFee)}
                          </span>
                        </Td>
                        <Td align="right">
                          <span className={cn('font-mono text-xs font-black', isEnCours ? 'text-rose-800' : 'text-emerald-800')}>
                            {formatTnd(t.netAmount)}
                          </span>
                        </Td>
                        <Td>
                          {t.validated ? (
                            <span className="inline-flex items-center text-[10px] font-semibold bg-emerald-50 text-emerald-800 px-2.5 py-0.5 rounded-full border border-emerald-200 print-badge-valid">
                              Clôturé (Validé)
                            </span>
                          ) : (
                            <span className="inline-flex items-center text-[10px] font-black bg-rose-100 text-rose-800 px-2.5 py-0.5 rounded-full border border-rose-300 shadow-xs print-badge-encours">
                              En cours (Non clôturé)
                            </span>
                          )}
                        </Td>
                        <Td className="no-print">
                          <Button
                            type="button"
                            variant="outline"
                            size="xs"
                            onClick={() => setSelectedRunsheetId(t.idRunsheet)}
                            className="gap-1 font-semibold text-xs border-emerald-300 text-emerald-900 hover:bg-emerald-50"
                          >
                            <FileText className="h-3 w-3 text-emerald-700" />
                            <span>Bordereau A4</span>
                          </Button>
                        </Td>
                      </Tr>
                    );
                  })}

                  {/* Summary Totals Row */}
                  <Tr className="font-bold bg-cream/70 border-t-2 border-emerald-700">
                    <Td colSpan={4}>
                      <span className="font-mono font-black text-xs text-ink uppercase">
                        TOTAL ({tours.length} tournées : {totalValidated} validées / {totalInProgress} en cours)
                      </span>
                    </Td>
                    <Td align="right">
                      <span className="font-mono font-black text-xs text-ink">
                        {totalPackages.toLocaleString('fr-FR')}
                      </span>
                    </Td>
                    <Td align="right">
                      <span className="font-mono font-black text-xs text-emerald-800">
                        {totalDelivered.toLocaleString('fr-FR')}
                      </span>
                    </Td>
                    <Td align="right">
                      <span className="font-mono font-bold text-xs text-ink">
                        {deliveryPct} %
                      </span>
                    </Td>
                    <Td align="right">
                      <span className="font-mono font-black text-xs text-blue-900">
                        {formatTnd(totalDeliveredCod)}
                      </span>
                    </Td>
                    <Td align="right">
                      <span className="font-mono font-black text-xs text-amber-700">
                        {formatTnd(totalDeliveryFee)}
                      </span>
                    </Td>
                    <Td align="right">
                      <span className="font-mono font-black text-xs text-emerald-800">
                        {formatTnd(totalNet)}
                      </span>
                    </Td>
                    <Td colSpan={2}>
                      <span className="text-[10px] text-ink-soft font-semibold">Net dû à MaPara</span>
                    </Td>
                  </Tr>
                </TBody>
              </Table>
            </TableShell>
          </div>
        )}
      </Panel>

      {/* 5. Official A4 Runsheet Bordereau Modal (When clicking a runsheet) */}
      {selectedRunsheetId !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-xs p-4 overflow-y-auto no-print">
          <div className="relative w-full max-w-4xl bg-white rounded-2xl shadow-2xl border border-slate-300 p-6 my-8 max-h-[90vh] overflow-y-auto">
            {/* Modal Top Actions */}
            <div className="flex items-center justify-between pb-4 mb-4 border-b border-slate-200">
              <div className="flex items-center gap-2">
                <span className="font-mono font-bold text-xs bg-emerald-800 text-white px-2.5 py-1 rounded-md">
                  Runsheet #{selectedRunsheetId}
                </span>
                <span className="text-sm font-bold text-slate-800">
                  {detailHeader?.livreurNom ?? 'Tournée Livreur'} · {formatDate(detailHeader?.tourDate)}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => window.print()}
                  className="gap-1.5 font-bold bg-emerald-700 hover:bg-emerald-800 text-white shadow-xs"
                >
                  <Printer className="h-4 w-4" />
                  <span>Imprimer A4</span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSelectedRunsheetId(null)}
                  className="gap-1 text-slate-600 hover:text-slate-900"
                >
                  <X className="h-4 w-4" />
                  <span>Fermer</span>
                </Button>
              </div>
            </div>

            {detailQuery.isLoading ? (
              <div className="py-12 text-center">
                <Loading label="Chargement des colis de la tournée…" />
              </div>
            ) : detailHeader ? (
              <div className="space-y-6">
                {/* Official A4 Sheet Content */}
                <div className="border-b-2 border-slate-900 pb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <div className="h-9 w-9 rounded-lg bg-emerald-700 text-white flex items-center justify-center font-black text-lg">
                        M
                      </div>
                      <div>
                        <h1 className="text-xl font-black text-slate-900 tracking-tight leading-none uppercase">
                          ILYANE EASYPARA
                        </h1>
                        <span className="text-[10px] font-semibold text-slate-600 tracking-wider uppercase block mt-0.5">
                          MaPara Tunisie • Parapharmacie &amp; Logistique
                        </span>
                      </div>
                    </div>
                    <div className="text-[10px] text-slate-500 mt-2 font-mono">
                      La Marsa, Tunisie • MF: 1815872F • contact@maparatunisie.tn
                    </div>
                  </div>

                  <div className="sm:text-right border-l-2 sm:border-l-0 border-emerald-600 pl-3 sm:pl-0">
                    <span className="inline-block px-2.5 py-1 rounded bg-slate-900 text-white font-mono text-xs font-black tracking-wider uppercase">
                      Bordereau de Tournée (Runsheet)
                    </span>
                    <div className="text-sm font-black font-mono text-slate-900 mt-1">
                      N° : RS-{detailHeader.idRunsheet}
                    </div>
                    <div className="text-[11px] text-slate-600 font-medium">
                      Date d'émission : <b>{formatDate(currentDate.toISOString())}</b>
                    </div>
                  </div>
                </div>

                {/* Runsheet Info Box */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs">
                  <div>
                    <span className="text-[10px] font-bold text-slate-500 uppercase block">Livreur</span>
                    <div className="font-bold text-slate-900 text-sm">{detailHeader.livreurNom ?? '—'}</div>
                    {detailHeader.matricule && (
                      <div className="text-slate-500 font-mono text-[10px]">{detailHeader.matricule}</div>
                    )}
                  </div>
                  <div>
                    <span className="text-[10px] font-bold text-slate-500 uppercase block">Date Tournée</span>
                    <div className="font-bold text-slate-900 font-mono">{formatDate(detailHeader.tourDate)}</div>
                    <div className="text-slate-500">{detailHeader.agence ?? 'Agence centrale'}</div>
                  </div>
                  <div>
                    <span className="text-[10px] font-bold text-slate-500 uppercase block">Colis Livrés</span>
                    <div className="font-bold text-emerald-800 font-mono text-sm">
                      {detailHeader.deliveredCount} / {detailHeader.packageCount} colis
                    </div>
                    <div className="text-slate-500 text-[10px]">
                      {detailHeader.packageCount > 0
                        ? `${Math.round((detailHeader.deliveredCount / detailHeader.packageCount) * 100)} % de réussite`
                        : '0 %'}
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-[10px] font-bold text-slate-500 uppercase block">Net à Reverser</span>
                    <div className="font-black text-emerald-800 font-mono text-base">
                      {formatTnd(detailHeader.netAmount)}
                    </div>
                    <div className="text-slate-500 text-[10px] font-mono">
                      COD {formatTnd(detailHeader.deliveredCod)} − Frais {formatTnd(detailHeader.deliveryFee)}
                    </div>
                  </div>
                </div>

                {/* Parcels Table */}
                <div>
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-2">
                    Détail des Colis Embarqués ({detailParcels.length} colis)
                  </h3>
                  <div className="border border-slate-200 rounded-lg overflow-hidden">
                    <table className="w-full text-xs text-left border-collapse">
                      <thead>
                        <tr className="bg-slate-100 border-b border-slate-200 text-[11px] font-bold text-slate-700">
                          <th className="p-2">Code barre</th>
                          <th className="p-2">Client</th>
                          <th className="p-2">Téléphone</th>
                          <th className="p-2">Ville</th>
                          <th className="p-2 text-right">COD</th>
                          <th className="p-2 text-center">État</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detailParcels.map((p, idx) => {
                          const code = p.codeBarre || (p as { code_barre?: string }).code_barre || '—';
                          const client = p.clientName ?? (p as { nom?: string }).nom ?? '—';
                          const phone = p.clientPhone ?? (p as { tel?: string }).tel ?? '—';
                          const loc = p.location ?? (p as { ville?: string }).ville ?? '—';
                          const price = p.shiparonPrice ?? (p as { prix?: number }).prix ?? 0;
                          const etat = p.shiparonEtat ?? (p as { etat?: number }).etat ?? 0;
                          const label = p.shiparonEtatLabel || (p as { etat_label?: string }).etat_label || (etat === 2 || etat === 10 || etat === 32 ? 'Livré' : 'En cours');
                          const isDelivered = etat === 2 || etat === 10 || etat === 32;

                          return (
                            <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50">
                              <td className="p-2 font-mono font-bold text-slate-900">{code}</td>
                              <td className="p-2">{client}</td>
                              <td className="p-2 font-mono text-slate-600">{phone}</td>
                              <td className="p-2">{loc}</td>
                              <td className="p-2 text-right font-mono font-bold text-slate-900">
                                {formatTnd(Number(price))}
                              </td>
                              <td className="p-2 text-center">
                                <span className={cn(
                                  'px-2 py-0.5 rounded-full text-[10px] font-bold',
                                  isDelivered
                                    ? 'bg-emerald-100 text-emerald-800'
                                    : 'bg-rose-100 text-rose-800'
                                )}>
                                  {label}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Legal Signature Box */}
                <div className="grid grid-cols-2 gap-8 pt-4 border-t border-slate-200 text-xs">
                  <div>
                    <span className="font-bold text-slate-700 block mb-1">Visa &amp; Signature Livreur :</span>
                    <div className="h-16 border border-dashed border-slate-300 rounded-lg p-2 text-[10px] text-slate-400">
                      Signature et mention « Lu et approuvé »
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="font-bold text-slate-700 block mb-1">Cachet &amp; Visa MaPara :</span>
                    <div className="h-16 border border-dashed border-slate-300 rounded-lg p-2 text-[10px] text-slate-400 text-right">
                      Pour Ilyane Easypara (Validation Caisse)
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}
